import { QuestionState } from '../render-store/render.store';

import {
  ComposedConditionSet,
  QuestionType,
} from '@hyper-inferno/survey-elements-core';

import { ClickToRankHandler } from '@hyper-inferno/click-to-rank-handler';
import { DropDownMultipleChoiceHandler } from '@hyper-inferno/drop-down-multiple-choice-handler';
import { DropDownSingleChoiceHandler } from '@hyper-inferno/drop-down-single-choice-handler';
import { ImageHandler } from '@hyper-inferno/image-handler';
import { MultipleChoiceHandler } from '@hyper-inferno/multiple-choice-handler';
import { NumericHandler } from '@hyper-inferno/numeric-handler';
import { OpenEndedHandler } from '@hyper-inferno/open-ended-handler';
import { SingleChoiceHandler } from '@hyper-inferno/single-choice-handler';

import {
  OPERATOR_FUNCTIONS,
  QuestionHandler,
} from '@hyper-inferno/survey-bot-core';
import { VideoHandler } from '@hyper-inferno/video-handler';
import { VoiceHandler } from '@hyper-inferno/voice-handler';
import { LocationHandler } from '@hyper-inferno/location-handler';
import { DisplayHandler } from '@hyper-inferno/display-handler';

const handlers: Record<string, QuestionHandler<any>> = {
  [QuestionType.OPEN_ENDED]: new OpenEndedHandler(),
  [QuestionType.SINGLE_CHOICE]: new SingleChoiceHandler(),
  [QuestionType.MULTIPLE_CHOICE]: new MultipleChoiceHandler(),
  [QuestionType.DROP_DOWN_SINGLE_CHOICE]: new DropDownSingleChoiceHandler(),
  [QuestionType.DROP_DOWN_MULTIPLE_CHOICE]: new DropDownMultipleChoiceHandler(),
  [QuestionType.CLICK_TO_RANK]: new ClickToRankHandler(),
  [QuestionType.VOICE]: new VoiceHandler(),
  [QuestionType.VIDEO]: new VideoHandler(),
  [QuestionType.NUMERIC]: new NumericHandler(),
  [QuestionType.IMAGE]: new ImageHandler(),
  [QuestionType.DISPLAY]: new DisplayHandler(),
  [QuestionType.LOCATION]: new LocationHandler(),
};

export function evaluateManifest(
  manifest: ComposedConditionSet,
  registry: Record<string, QuestionState>,
): boolean {
  if (!manifest || !manifest.conditions.length) return true;

  const results = manifest.conditions.map((condition) => {
    // Priority: 1. Live state from store, 2. Initial dependency from manifest

    const id = condition.condition_element_id.toString();

    // 1. Check registry (current page live data)
    // 2. Fallback to manifest dependencies (previous page data)

    const rawValue = registry[id]
      ? registry[id].data
      : manifest.dependencies[condition.condition_element_id];

    const handler = handlers[condition.element.type];
    if (!handler || typeof handler.getValue !== 'function') {
      console.warn(`No getValue implementation for ${condition.element.type}`);
      return false;
    }

    if (!rawValue) {
      return false;
    }

    // Get user's answer via the specific handler
    const userValue = handler.getValue(condition.element, rawValue);

    // Your logic: is expects an array, numeric ops handle strings/numbers
    const operatorFn = OPERATOR_FUNCTIONS[condition.operator];
    if (!operatorFn) return false;

    return operatorFn(userValue, condition);
  });

  return manifest.logical_operator === 'AND'
    ? results.every((res) => res === true)
    : results.some((res) => res === true);
}
